import type {
  PlaceholderShapeKind,
  Point2,
  ResolvedPlaceholderShape,
} from "./types";

const TAU = Math.PI * 2;

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function dist(a: Point2, b: Point2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Wavy displacement along an open edge A→B, in the inward normal direction.
 * Uses sin(πt) envelope so endpoints stay fixed (clean joins at corners).
 * Two superposed frequencies for a more organic edge ripple.
 */
function wavifyEdge(
  a: Point2,
  b: Point2,
  samples: number,
  amplitude: number,
  edgeWeight: number,
  edgePhase: number
): Point2[] {
  if (samples < 2) samples = 2;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const cyclesBase = Math.max(1.25, (len / 120) * 4);
  const out: Point2[] = [];
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    if (i === 0) {
      out.push({ x: a.x, y: a.y });
      continue;
    }
    if (i === samples) {
      out.push({ x: b.x, y: b.y });
      continue;
    }
    const envelope = Math.sin(Math.PI * t);
    const along = t * len;
    const w1 = Math.sin((along / len) * TAU * cyclesBase + edgePhase);
    const w2 =
      0.42 * Math.sin((along / len) * TAU * (cyclesBase * 2.3) + edgePhase * 1.7 + 0.8);
    const wobble = amplitude * edgeWeight * envelope * (w1 + w2);
    out.push({
      x: lerp(a.x, b.x, t) + nx * wobble,
      y: lerp(a.y, b.y, t) + ny * wobble,
    });
  }
  return out;
}

function sampleArcRing(
  cx: number,
  cy: number,
  r: number,
  a0: number,
  a1: number,
  n: number
): Point2[] {
  const out: Point2[] = [];
  for (let k = 1; k <= n; k++) {
    const ang = lerp(a0, a1, k / n);
    out.push({
      x: cx + r * Math.cos(ang),
      y: cy + r * Math.sin(ang),
    });
  }
  return out;
}

function mergeRing(segments: Point2[][]): Point2[] {
  const ring: Point2[] = [];
  for (let s = 0; s < segments.length; s++) {
    const seg = segments[s];
    if (s === 0) ring.push(...seg);
    else ring.push(...seg.slice(1));
  }
  return ring;
}

function defaultEdgeWeights(edgeCount: number, byEdge?: number[]): number[] {
  const w = byEdge?.length === edgeCount ? byEdge : undefined;
  return Array.from({ length: edgeCount }, (_, i) =>
    clamp(w?.[i] ?? 1, 0, 4)
  );
}

/** Regular N-gon vertices around origin, first vertex at -90° (top), CCW when y increases downward — angles increase CW in standard math; we use angle from center with canvas y down = sin signs adjusted). */
function regularPolygonVertices(
  n: number,
  radius: number,
  startAngle: number
): Point2[] {
  const pts: Point2[] = [];
  for (let i = 0; i < n; i++) {
    const a = startAngle + (i * TAU) / n;
    pts.push({
      x: radius * Math.cos(a),
      y: radius * Math.sin(a),
    });
  }
  return pts;
}

function applyCornerRounding(
  ring: Point2[],
  roundedness: number
): Point2[] {
  const n = ring.length;
  if (n < 3 || roundedness <= 0.001) return ring;
  const out: Point2[] = [];
  for (let i = 0; i < n; i++) {
    const prev = ring[(i + n - 1) % n];
    const cur = ring[i];
    const next = ring[(i + 1) % n];
    const d1 = dist(prev, cur);
    const d2 = dist(cur, next);
    const cut = Math.min(d1, d2) * 0.5 * clamp(roundedness, 0, 1);
    if (cut < 0.5) {
      out.push(cur);
      continue;
    }
    const t1 = cut / d1;
    const t2 = cut / d2;
    out.push({
      x: lerp(cur.x, prev.x, t1),
      y: lerp(cur.y, prev.y, t1),
    });
    out.push({
      x: lerp(cur.x, next.x, t2),
      y: lerp(cur.y, next.y, t2),
    });
  }
  return out;
}

function buildStarVertices(
  points: number,
  outerR: number,
  innerR: number
): Point2[] {
  const n = points;
  const verts: Point2[] = [];
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const a = -Math.PI / 2 + (i * Math.PI) / n;
    verts.push({ x: r * Math.cos(a), y: r * Math.sin(a) });
  }
  return verts;
}

/** Heart curve (scaled) — classic two-lobe parameterization, roughly fits [-1,1]×[-1.2,0.8]. */
function heartSamples(samples: number, scale: number): Point2[] {
  const pts: Point2[] = [];
  for (let i = 0; i <= samples; i++) {
    const t = (i / samples) * TAU;
    const x = scale * 16 * Math.sin(t) ** 3;
    const y =
      scale *
      (13 * Math.cos(t) -
        5 * Math.cos(2 * t) -
        2 * Math.cos(3 * t) -
        Math.cos(4 * t));
    pts.push({ x, y: -y });
  }
  // Normalize to fit in [-hw,-hh] bounding box (uniform scale)
  let minx = Infinity,
    maxx = -Infinity,
    miny = Infinity,
    maxy = -Infinity;
  for (const p of pts) {
    minx = Math.min(minx, p.x);
    maxx = Math.max(maxx, p.x);
    miny = Math.min(miny, p.y);
    maxy = Math.max(maxy, p.y);
  }
  const cw = maxx - minx || 1;
  const ch = maxy - miny || 1;
  const cx = (minx + maxx) / 2;
  const cy = (miny + maxy) / 2;
  return pts.map((p) => {
    const nx = (p.x - cx) / cw;
    const ny = (p.y - cy) / ch;
    return { x: nx * 2, y: ny * 2 };
  });
}

export interface BuildBoundaryOptions {
  /** Samples per straight edge before waviness */
  edgeSamples?: number;
  /** Heart / ellipse angular samples */
  curveSamples?: number;
}

const DEF_EDGE = 24;
const DEF_CURVE = 128;

/** Horizontal shear (top shifted vs bottom); uniformly scaled to stay inside the slot box. */
function parallelogramInBoundingBox(
  hw: number,
  hh: number,
  skewNorm: number
): Point2[] {
  const t = clamp(skewNorm, -1, 1);
  const rawShear = t * hw;
  const verts: Point2[] = [
    { x: -hw + rawShear, y: -hh },
    { x: hw + rawShear, y: -hh },
    { x: hw - rawShear, y: hh },
    { x: -hw - rawShear, y: hh },
  ];
  let minx = Infinity,
    maxx = -Infinity,
    miny = Infinity,
    maxy = -Infinity;
  for (const v of verts) {
    minx = Math.min(minx, v.x);
    maxx = Math.max(maxx, v.x);
    miny = Math.min(miny, v.y);
    maxy = Math.max(maxy, v.y);
  }
  const bw = maxx - minx || 1;
  const bh = maxy - miny || 1;
  const scale = Math.min((2 * hw) / bw, (2 * hh) / bh);
  const cx = (minx + maxx) / 2;
  const cy = (miny + maxy) / 2;
  return verts.map((v) => ({
    x: (v.x - cx) * scale,
    y: (v.y - cy) * scale,
  }));
}

/**
 * Closed polyline approximating the placeholder aperture, in centered slot coordinates
 * (origin = slot center, x ∈ [-w/2,w/2], y ∈ [-h/2,h/2] conceptually; points may extend to that box).
 */
export function buildPlaceholderBoundary(
  kind: PlaceholderShapeKind,
  width: number,
  height: number,
  resolved: ResolvedPlaceholderShape,
  options: BuildBoundaryOptions = {}
): Point2[] {
  const w = Math.max(1, width);
  const h = Math.max(1, height);
  const hw = w / 2;
  const hh = h / 2;
  const { roundedness, waviness, wavinessByEdge, starPoints, skew } =
    resolved.params;
  const edgeSamples = options.edgeSamples ?? DEF_EDGE;
  const curveSamples = options.curveSamples ?? DEF_CURVE;

  let wavAmp =
    waviness * Math.min(w, h) * (0.038 + 0.062 * clamp(waviness, 0, 1));
  if (
    kind === "hexagon" ||
    kind === "star" ||
    kind === "heart" ||
    kind === "parallelogram"
  ) {
    wavAmp = 0;
  }
  const phases = [0, 1.7, 3.1, 0.8, 2.2, 4.0, 1.1];

  let ring: Point2[] = [];

  switch (kind) {
    case "rect":
    case "roundedRect": {
      const rMax = Math.min(hw, hh);
      const rCorner = rMax * clamp(roundedness, 0, 1);
      const useRoundCorners = rCorner >= 0.5 && roundedness >= 0.01;

      if (useRoundCorners) {
        const weights = defaultEdgeWeights(4, wavinessByEdge);
        const nArc = Math.max(8, Math.round(edgeSamples / 3));
        const arcs: Array<{ cx: number; cy: number; a0: number; a1: number }> =
          [
            { cx: hw - rCorner, cy: -hh + rCorner, a0: -Math.PI / 2, a1: 0 },
            { cx: hw - rCorner, cy: hh - rCorner, a0: 0, a1: Math.PI / 2 },
            { cx: -hw + rCorner, cy: hh - rCorner, a0: Math.PI / 2, a1: Math.PI },
            {
              cx: -hw + rCorner,
              cy: -hh + rCorner,
              a0: Math.PI,
              a1: (3 * Math.PI) / 2,
            },
          ];
        const lines: Array<[Point2, Point2]> = [
          [{ x: -hw + rCorner, y: -hh }, { x: hw - rCorner, y: -hh }],
          [{ x: hw, y: -hh + rCorner }, { x: hw, y: hh - rCorner }],
          [{ x: hw - rCorner, y: hh }, { x: -hw + rCorner, y: hh }],
          [{ x: -hw, y: hh - rCorner }, { x: -hw, y: -hh + rCorner }],
        ];
        const segs: Point2[][] = [];
        for (let side = 0; side < 4; side++) {
          const [la, lb] = lines[side];
          if (wavAmp > 1e-6) {
            segs.push(
              wavifyEdge(
                la,
                lb,
                edgeSamples,
                wavAmp,
                weights[side],
                phases[side % phases.length]
              )
            );
          } else {
            segs.push([la, lb]);
          }
          segs.push(
            sampleArcRing(
              arcs[side].cx,
              arcs[side].cy,
              rCorner,
              arcs[side].a0,
              arcs[side].a1,
              nArc
            )
          );
        }
        ring = mergeRing(segs);
        break;
      }

      const corners: Point2[] = [
        { x: -hw, y: -hh },
        { x: hw, y: -hh },
        { x: hw, y: hh },
        { x: -hw, y: hh },
      ];
      const weights = defaultEdgeWeights(4, wavinessByEdge);
      ring = mergeRing(
        corners.map((_, i) =>
          wavifyEdge(
            corners[i],
            corners[(i + 1) % 4],
            edgeSamples,
            wavAmp,
            weights[i],
            phases[i % phases.length]
          )
        )
      );
      break;
    }
    case "ellipse": {
      const rx = hw;
      const ry = hh;
      const n = Math.max(28, Math.min(curveSamples, 128));
      const verts: Point2[] = [];
      for (let i = 0; i < n; i++) {
        const ang = (i / n) * TAU;
        verts.push({ x: rx * Math.cos(ang), y: ry * Math.sin(ang) });
      }
      const weights = defaultEdgeWeights(n, wavinessByEdge);
      ring = mergeRing(
        verts.map((_, i) =>
          wavifyEdge(
            verts[i],
            verts[(i + 1) % n],
            Math.max(8, Math.floor(edgeSamples / 2)),
            wavAmp,
            weights[i],
            phases[i % phases.length]
          )
        )
      );
      break;
    }
    case "hexagon": {
      // Flat-top hexagon inscribed in box
      const R = Math.min((2 * hw) / Math.sqrt(3), hh);
      let verts = regularPolygonVertices(6, R * 0.98, -Math.PI / 2);
      verts = applyCornerRounding(verts, roundedness);
      const weights = defaultEdgeWeights(verts.length, wavinessByEdge);
      ring = mergeRing(
        verts.map((_, i) =>
          wavifyEdge(
            verts[i],
            verts[(i + 1) % verts.length],
            edgeSamples,
            wavAmp,
            weights[i],
            phases[i % phases.length]
          )
        )
      );
      break;
    }
    case "star": {
      const sp = clamp(Math.round(starPoints), 5, 12);
      const ro = Math.min(hw, hh) * 0.92;
      const inner =
        ro *
        (0.35 + (1 - clamp(roundedness, 0, 1)) * 0.2);
      let verts = buildStarVertices(sp, ro, inner);
      verts = applyCornerRounding(verts, roundedness * 0.5);
      const weights = defaultEdgeWeights(verts.length, wavinessByEdge);
      ring = mergeRing(
        verts.map((_, i) =>
          wavifyEdge(
            verts[i],
            verts[(i + 1) % verts.length],
            edgeSamples,
            wavAmp,
            weights[i],
            phases[i % phases.length]
          )
        )
      );
      break;
    }
    case "rhombus": {
      const verts: Point2[] = [
        { x: 0, y: -hh },
        { x: hw, y: 0 },
        { x: 0, y: hh },
        { x: -hw, y: 0 },
      ];
      const rv = applyCornerRounding(verts, roundedness);
      const weights = defaultEdgeWeights(rv.length, wavinessByEdge);
      ring = mergeRing(
        rv.map((_, i) =>
          wavifyEdge(
            rv[i],
            rv[(i + 1) % rv.length],
            edgeSamples,
            wavAmp,
            weights[i],
            phases[i % phases.length]
          )
        )
      );
      break;
    }
    case "heart": {
      let hpts = heartSamples(curveSamples, 0.06).map((p) => ({
        x: p.x * hw,
        y: p.y * hh,
      }));
      if (hpts.length > 2) {
        const a = hpts[0];
        const b = hpts[hpts.length - 1];
        if (Math.hypot(a.x - b.x, a.y - b.y) < 1e-4) {
          hpts = hpts.slice(0, -1);
        }
      }
      const hn = hpts.length;
      if (hn < 3) {
        ring = hpts;
        break;
      }
      const weights = defaultEdgeWeights(hn, wavinessByEdge);
      ring = mergeRing(
        hpts.map((_, i) =>
          wavifyEdge(
            hpts[i],
            hpts[(i + 1) % hn],
            Math.max(8, Math.floor(edgeSamples / 2)),
            wavAmp,
            weights[i],
            phases[i % phases.length]
          )
        )
      );
      break;
    }
    case "parallelogram": {
      const verts = parallelogramInBoundingBox(hw, hh, skew);
      const pv = applyCornerRounding(verts, roundedness);
      ring = mergeRing(
        pv.map((_, i) => [pv[i], pv[(i + 1) % pv.length]])
      );
      break;
    }
    default:
      ring = [
        { x: -hw, y: -hh },
        { x: hw, y: -hh },
        { x: hw, y: hh },
        { x: -hw, y: hh },
      ];
  }

  // Ensure closed (last ~= first)
  if (ring.length > 1) {
    const a = ring[0];
    const b = ring[ring.length - 1];
    if (Math.hypot(a.x - b.x, a.y - b.y) < 1e-4) ring.pop();
  }
  return ring;
}

/** Point-in-polygon (ray cast), coordinates in same space as boundary. */
export function pointInPolygon(x: number, y: number, poly: Point2[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x;
    const yi = poly[i].y;
    const xj = poly[j].x;
    const yj = poly[j].y;
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function appendBoundaryToCanvasPath(
  ctx: CanvasRenderingContext2D,
  poly: Point2[]
): void {
  if (poly.length === 0) return;
  ctx.moveTo(poly[0].x, poly[0].y);
  for (let i = 1; i < poly.length; i++) {
    ctx.lineTo(poly[i].x, poly[i].y);
  }
  ctx.closePath();
}

export function boundaryToPath2D(poly: Point2[]): Path2D {
  const p = new Path2D();
  if (poly.length === 0) return p;
  p.moveTo(poly[0].x, poly[0].y);
  for (let i = 1; i < poly.length; i++) {
    p.lineTo(poly[i].x, poly[i].y);
  }
  p.closePath();
  return p;
}

/** SVG path d (absolute coords), origin top-left of slot: centers the shape. */
export function boundaryToSvgPathD(
  poly: Point2[],
  width: number,
  height: number
): string {
  const ox = width / 2;
  const oy = height / 2;
  if (poly.length === 0) return "";
  const parts: string[] = [];
  parts.push(`M ${(poly[0].x + ox).toFixed(2)} ${(poly[0].y + oy).toFixed(2)}`);
  for (let i = 1; i < poly.length; i++) {
    parts.push(`L ${(poly[i].x + ox).toFixed(2)} ${(poly[i].y + oy).toFixed(2)}`);
  }
  parts.push("Z");
  return parts.join(" ");
}

/**
 * CSS clip-path using path() — coordinates as % of box (0–100).
 * `evenodd` helps self-intersections from extreme waviness.
 */
export function boundaryToCssClipPathValue(
  poly: Point2[],
  width: number,
  height: number
): string {
  const ox = width / 2;
  const oy = height / 2;
  if (poly.length === 0) return "none";
  const toPct = (lx: number, ly: number) => {
    const px = ((lx + ox) / width) * 100;
    const py = ((ly + oy) / height) * 100;
    return `${px.toFixed(3)}% ${py.toFixed(3)}%`;
  };
  const first = toPct(poly[0].x, poly[0].y);
  const rest = poly
    .slice(1)
    .map((p) => `L ${toPct(p.x, p.y)}`)
    .join(" ");
  return `path(evenodd, 'M ${first} ${rest} Z')`;
}
